import { useEffect, useCallback, useMemo } from 'react';
import { captureStore, PlayerAccessRights } from './store';
import { useAnimationFrame } from '../useAnimationFrame';

declare global {
  interface Navigator {
    keyboard: { lock: () => Promise<undefined> }; // Replace 'any' with the type of 'keyboard' if known
  }
}

export const useGamepads = (enabled: boolean, callback: (event: Event) => void) => {
  useAnimationFrame((t: number) => {
    if (!enabled) return;
    const g = navigator.getGamepads();
    // Convert the gamepad info above into the right format to be an "event"
    const event = new CustomEvent('gamepad', {
      detail: g,
    });
    callback(event);
  });
};

// skipGamepad is used to prevent physical gamepad input when we are using the mobile gamepad
export const Input = (props: PlayerAccessRights & { skipGamepad?: boolean }) => {
  const { mouse, keyboard, touch, gamepad, skipGamepad } = props;

  const callback = useCallback((event: Event | CustomEvent) => {
    captureStore.addEvent(event);
    event.preventDefault();
  }, []);

  useGamepads(
    !!gamepad,
    skipGamepad
      ? () => {
          return;
        }
      : callback,
  );

  const eventTypes = useMemo(() => {
    const mouseEvents = ['mousedown', 'mouseup', 'mousemove', 'wheel'];
    const keyboardEvents = ['keydown', 'keyup'];
    const touchEvents = ['touchstart', 'touchend', 'touchmove', 'touchcancel'];
    const gamepadEvents = ['gamepad'];

    const types: string[] = [];
    if (mouse) {
      types.push(...mouseEvents);
    }
    if (keyboard) {
      types.push(...keyboardEvents);
    }
    if (touch) {
      types.push(...touchEvents);
    }
    if (gamepad) {
      types.push(...gamepadEvents);
    }
    return types;
  }, [mouse, keyboard, touch, gamepad]);

  useEffect(() => {
    console.log('InputCapture: add captured events', eventTypes);
    eventTypes.forEach((e) => {
      window.addEventListener(e, callback as EventListener, { passive: false });
    });
    return () => {
      console.log('InputCapture: remove captured events');
      eventTypes.forEach((e) => {
        window.removeEventListener(e, callback as EventListener);
      });
    };
  }, [eventTypes, callback]);

  return null;
};
